import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import Container from '../base/Container';
import UsageAndBilling from '../usageandbilling/UsageAndBilling';
import Organisations from '../users/OrganisationsPage';
import DiagnosticDashboard from '../diagnostics/DiagnosticDashboard';
import RecycleBin from '../recycle-bin/RecycleBin';
import { TestSuitesContent } from '../testSuites/TestSuitesPage';
import { ReleaseNotesPanel } from '../release-notes';
import { rememberSectionPath } from '../hooks/useSessionState';

const TAB_SLUGS = [
  'analytics',
  'manage-orgs',
  'manage-ontology',
  'feedback',
  'diagnostics',
  'recycle-bin',
  'test-suites',
  'release-notes',
] as const;

type TabSlug = (typeof TAB_SLUGS)[number];


function slugToIndex(slug: string | null): number {
  const idx = TAB_SLUGS.indexOf((slug ?? '') as TabSlug);
  return idx >= 0 ? idx : 0;
}

export default function GlobalAdminPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(() =>
    slugToIndex(new URLSearchParams(location.search).get('tab')),
  );

  useEffect(() => {
    const slug = new URLSearchParams(location.search).get('tab');
    setTabValue(slugToIndex(slug));
    // So the sidebar "Global Admin" nav link can return to this exact tab.
    rememberSectionPath('global-admin', `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(`/global-admin?tab=${TAB_SLUGS[newValue]}`, { replace: true });
  };

  return (
    <Container>
      <Box sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="Usage Analytics" />
          <Tab label="Manage Organisations" />
          <Tab label="Manage Ontology" />
          <Tab label="Feedback" />
          <Tab label="Diagnostics" />
          <Tab label="Recycle Bin" />
          <Tab label="Test Suites" />
          <Tab label="Release Notes" />
        </Tabs>

        {tabValue === 0 && <UsageAndBilling isAdmin={true} />}

        {tabValue === 1 && <Organisations isAdmin={true} view="global-admin-orgs" />}
        {tabValue === 2 && <Organisations isAdmin={true} view="global-admin-ontology" />}
        {tabValue === 3 && <Organisations isAdmin={true} view="global-admin-feedback" />}

        {tabValue === 4 && <DiagnosticDashboard />}

        {tabValue === 5 && <RecycleBin />}

        {tabValue === 6 && <TestSuitesContent />}

        {tabValue === 7 && <ReleaseNotesPanel />}
      </Box>
    </Container>
  );
}
